import { CheckIcon, StarIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { API_URL } from '../../utils/constants';
import './PhotoReview.css';

const PhotoReview = () => {
    const { sessionId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const userEmail = searchParams.get('user_email');
    
    const [candidates, setCandidates] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [rating, setRating] = useState(0);
    const [review, setReview] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (sessionId && userEmail) {
            fetchCandidates();
        }
    }, [sessionId, userEmail]);

    const fetchCandidates = async () => {
        try {
            setLoading(true);
            
            // Fetch both candidate photos and photos needing location
            const [candidatesResponse, needsLocationResponse] = await Promise.all([
                fetch(`${API_URL}/api/recommendations/temp-photos/candidates/${sessionId}?user_email=${encodeURIComponent(userEmail)}`),
                fetch(`${API_URL}/api/recommendations/temp-photos/no-place-found/${sessionId}?user_email=${encodeURIComponent(userEmail)}`)
            ]);
            
            let candidates = [];
            let needsLocationPhotos = [];

            // Process candidates response
            if (candidatesResponse.ok) {
                const candidatesData = await candidatesResponse.json();
                candidates = candidatesData.candidates || [];
            } else {
                console.warn(`⚠️ Failed to fetch candidates: ${candidatesResponse.status}`);
            }

            // Process needs location response
            if (needsLocationResponse.ok) {
                const needsLocationData = await needsLocationResponse.json();
                needsLocationPhotos = needsLocationData.photos || [];
            } else {
                console.warn(`⚠️ Failed to fetch photos needing location: ${needsLocationResponse.status}`);
            }

            // Combine all photos
            const allCandidates = [...candidates, ...needsLocationPhotos];
            setCandidates(allCandidates);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRatingChange = (newRating) => {
        setRating(newRating);
    };

    const handleSubmit = async () => {
        if (rating === 0) {
            alert('Please select a rating');
            return;
        }

        const currentCandidate = candidates[currentIndex];
        if (!currentCandidate) return;

        setSubmitting(true);
        try {
            console.log('Submitting recommendation with enrichment:', {
                photoId: currentCandidate.id,
                rating,
                review,
                placeId: currentCandidate.googlePlaceId,
                businessName: currentCandidate.businessName
            });

            const response = await fetch(`${API_URL}/api/recommendations/submit-with-enrichment`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sessionId,
                    photoId: currentCandidate.id,
                    placeId: currentCandidate.googlePlaceId,
                    rating,
                    review,
                    user_id: userEmail // This will be converted to Clerk ID lookup
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Submission failed');
            }

            const result = await response.json();
            console.log('✅ Recommendation submitted successfully:', result);
            
            // Show success message with API call info
            const message = result.isNewProvider 
                ? `✅ Recommendation submitted! (Details API called for new provider)`
                : `✅ Recommendation submitted! (Reused existing provider - no additional API cost)`;
            
            alert(message);

            // Move to next photo
            if (currentIndex < candidates.length - 1) {
                setCurrentIndex(currentIndex + 1);
                setRating(0);
                setReview('');
            } else {
                // All photos reviewed
                navigate('/');
            }
        } catch (err) {
            console.error('Error submitting recommendation:', err);
            alert(`Submission failed: ${err.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleSkip = () => {
        if (currentIndex < candidates.length - 1) {
            setCurrentIndex(currentIndex + 1);
            setRating(0);
            setReview('');
        } else {
            navigate('/');
        }
    };

    if (loading) {
        return (
            <div className="photo-review-container">
                <div className="loading">Loading photos...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="photo-review-container">
                <div className="error">Error: {error}</div>
            </div>
        );
    }

    if (candidates.length === 0) {
        return (
            <div className="photo-review-container">
                <div className="no-candidates">
                    <h2>No photos to review</h2>
                    <p>All photos have been processed or there are no candidates for review.</p>
                    <button onClick={() => navigate('/')} className="back-button">
                        Back to Home
                    </button>
                </div>
            </div>
        );
    }

    const currentCandidate = candidates[currentIndex];
    const progress = ((currentIndex + 1) / candidates.length) * 100;

    return (
        <div className="photo-review-container">
            <div className="header">
                <h1>Review Your Photos</h1>
                <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                </div>
                <p className="progress-text">
                    {currentIndex + 1} of {candidates.length} photos
                </p>
            </div>

            <div className="photo-content">
                <div className="photo-section">
                    <img 
                        src={`${API_URL}/api/photos/temp/${currentCandidate.id}`}
                        alt={currentCandidate.originalFilename}
                        className="photo-preview"
                        onError={(e) => {
                            e.target.src = '/placeholder-image.png';
                        }}
                    />
                </div>

                <div className="place-section">
                    <h2>{currentCandidate.businessName}</h2>
                    <p className="address">{currentCandidate.streetAddress}</p>
                    
                    <div className="rating-section">
                        <h3>Rate this place:</h3>
                        <div className="stars">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    type="button"
                                    className={`star ${rating >= star ? 'filled' : ''}`}
                                    onClick={() => handleRatingChange(star)}
                                >
                                    <StarIcon />
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="review-section">
                        <h3>Write a review (optional):</h3>
                        <textarea
                            value={review}
                            onChange={(e) => setReview(e.target.value)}
                            placeholder="Share your experience with this place..."
                            rows={4}
                            className="review-textarea"
                        />
                    </div>
                </div>
            </div>

            <div className="actions">
                <button 
                    onClick={handleSkip}
                    className="skip-button"
                    disabled={submitting}
                >
                    <XMarkIcon className="w-5 h-5" />
                    Skip
                </button>
                
                <button 
                    onClick={handleSubmit}
                    className="submit-button"
                    disabled={rating === 0 || submitting}
                >
                    <CheckIcon className="w-5 h-5" />
                    {submitting ? 'Submitting...' : 'Submit Review'}
                </button>
            </div>
        </div>
    );
};

export default PhotoReview; 